import React, { useMemo, useState } from 'react';
import { IoSearchSharp } from 'react-icons/io5';
import { FaLayerGroup } from 'react-icons/fa';
import { FilterConfig, FilterPlacement } from '../../../core/components/PageFilters';
import { getAreaOptions, getAreaValues } from '../listHelpers';
import { formatTemplateType } from '../models';

/**
 * The catalog's name and area filters, and the template list they narrow.
 *
 * Area is the AssetTemplate `type` value, so the options are whichever types
 * are in play at this site rather than a fixed list.
 */
export function useAssetFilters({
  intl,
  activeTemplates,
  deployedTemplateIds,
  cartMode,
}: {
  intl: any;
  activeTemplates: any[];
  deployedTemplateIds: number[];
  cartMode: boolean;
}) {
  const [siteFilters, setSiteFilters] = useState<Record<string, any>>({
    name: '',
    areas: [],
  });

  const siteFilterPredicates = useMemo(() => {
    const name = String(siteFilters.name || '').toLowerCase();
    const areas = (siteFilters.areas || []).map(String);
    const matchesName = (label: string) => !name || label.toLowerCase().includes(name);
    const matchesArea = (entity: any, rowType: 'template' | 'group' | 'item') =>
      !areas.length || getAreaValues(entity, rowType).some((value) => areas.includes(value));
    return { matchesArea, matchesName };
  }, [siteFilters]);

  // Normally the site's own catalogue: only what it has actually deployed.
  // Cart mode opens it up to every master template so more can be added.
  const siteManagerTemplates = useMemo(() => {
    const { matchesArea, matchesName } = siteFilterPredicates;
    const deployed = new Set(deployedTemplateIds);
    return activeTemplates.filter(
      (t: any) =>
        (cartMode || deployed.has(Number(t.id))) &&
        matchesName(t.name || '') &&
        matchesArea(t, 'template')
    );
  }, [activeTemplates, siteFilterPredicates, cartMode, deployedTemplateIds]);

  // Same filter components the Inventory page uses: chiclet search + selects.
  const siteFilterConfigs: FilterConfig[] = useMemo(() => {
    const areaOptions = getAreaOptions(activeTemplates).map((area: string) => ({
      value: area,
      text: formatTemplateType(area),
      selected: (siteFilters.areas || []).map(String).includes(area),
    }));
    return [
      {
        id: 'name',
        type: 'search',
        name: intl.formatMessage({ id: 'assets.list.name' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <IoSearchSharp />,
        debounceMs: 0,
        searchTerm: siteFilters.name,
        onChange: (value: string) => setSiteFilters((prev) => ({ ...prev, name: value || '' })),
      },
      {
        id: 'areas',
        type: 'select',
        name: intl.formatMessage({ id: 'assets.list.area' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaLayerGroup />,
        options: areaOptions,
        onChange: (values: string[]) => setSiteFilters((prev) => ({ ...prev, areas: values })),
      },
    ] as FilterConfig[];
  }, [intl, activeTemplates, siteFilters]);

  return { siteFilters, setSiteFilters, siteManagerTemplates, siteFilterConfigs };
}
